import { FC } from "react";
import {
  ArrayInput,
  Edit,
  EditProps,
  FormGroupContextProvider,
  FormWithRedirect,
  maxValue,
  minValue,
  NumberInput,
  ReferenceInput,
  required,
  SelectInput,
  SimpleFormIterator,
} from "react-admin";

import CustomCard from "../../components/CustomCard";
import CustomToolbar from "../../components/CustomToolbar";
import FormWrapper from "../../components/FormWrapper";
import ImageUploadInput from "../../components/ImageUploadInput";
import Spacing from "../../components/Spacing";
import useDisabledOnLoading from "../../hooks/useDisabledOnLoading";
import useGetEnumsAsChoices from "../../hooks/useGetEnumsAsChoices";
import { FormGroupNames } from "../../types/reactAdmin.types";
import { AppResource, Enums } from "../../types/resource.types";
import { validateFilesCount, validateFileSize } from "../../utils/validators";
import VariantsButton from "./components/VariantsButton";
import "./style/product.scss";
import { getTotalProducts, prepareProductProperties, prepareRAProperties } from "./utils";
import useProductsLocalizations from "./utils/useProductsLocalizations";

const CustomForm: FC<CustomEditProps> = (props) => {
  const { disabledStyles } = useDisabledOnLoading();
  const localizations = useProductsLocalizations(props);
  const genderEnums = useGetEnumsAsChoices<Enums>(Enums.productsGenders);
  const sizesEnums = useGetEnumsAsChoices<Enums>(Enums.productsSizes);
  const colorsEnums = useGetEnumsAsChoices<Enums>(Enums.productsColors);

  const product = {
    ...props.record,
    properties: prepareRAProperties(props.record.properties),
  };

  return (
    <FormWrapper>
      <FormGroupContextProvider name={FormGroupNames.productsCreate}>
        <FormWithRedirect
          {...props}
          record={product}
          redirect={`/${AppResource.products}`}
          render={(formProps) => {
            // console.log(formProps.form.getState());

            return (
              <form style={disabledStyles}>
                <CustomCard title="Details">
                  <Spacing>
                    {localizations?.[0]}
                    {localizations?.[1]}
                  </Spacing>

                  <Spacing>
                    {localizations?.[2]}
                    {localizations?.[3]}
                  </Spacing>

                  <Spacing>
                    <ReferenceInput
                      label="Category"
                      variant="outlined"
                      reference={AppResource.brandsCategories}
                      source="categoryId"
                      fullWidth
                      validate={required()}
                    >
                      <SelectInput optionText="title.en" />
                    </ReferenceInput>
                    <SelectInput
                      source="gender"
                      choices={genderEnums}
                      variant="outlined"
                      fullWidth
                      validate={required()}
                    />
                  </Spacing>

                  <Spacing>
                    <NumberInput variant="outlined" fullWidth source="price" validate={[required(), minValue(1)]} />
                    <NumberInput
                      variant="outlined"
                      fullWidth
                      source="discountPercentage"
                      min={0}
                      validate={[minValue(0), maxValue(100)]}
                    />
                  </Spacing>
                </CustomCard>

                <CustomCard title="Properties">
                  <ArrayInput source="properties" validate={required()}>
                    <SimpleFormIterator
                      addButton={<VariantsButton title="Add Variant" />}
                      removeButton={<VariantsButton add={false} />}
                      className="product-variants"
                    >
                      <SelectInput
                        source="color"
                        label="Color"
                        choices={colorsEnums}
                        variant="outlined"
                        validate={required()}
                      />
                      <ImageUploadInput
                        source="images"
                        multiple
                        validate={[required(), validateFileSize("images"), validateFilesCount("images")]}
                      />
                      <ArrayInput source="sizes" label="Sizes" validate={required()}>
                        <SimpleFormIterator
                          addButton={<VariantsButton title="Add Size" />}
                          removeButton={<VariantsButton add={false} />}
                          className="product-variants-sizes"
                        >
                          <SelectInput
                            source="size"
                            label="Size"
                            choices={sizesEnums}
                            variant="outlined"
                            validate={required()}
                          />
                          <NumberInput
                            variant="outlined"
                            label="Quantity"
                            source="quantity"
                            validate={[required(), minValue(0)]}
                          />
                        </SimpleFormIterator>
                      </ArrayInput>
                    </SimpleFormIterator>
                  </ArrayInput>
                </CustomCard>

                <CustomToolbar {...formProps} formName={FormGroupNames.productsCreate} />
              </form>
            );
          }}
        />
      </FormGroupContextProvider>
    </FormWrapper>
  );
};

const ProductsEdit: FC<EditProps> = (props) => {
  return (
    <Edit
      {...props}
      transform={(values) => {
        values.discountPercentage = values.discountPercentage ?? 0;
        values.totalItems = getTotalProducts(values.properties);
        values.properties = prepareProductProperties(values.properties);
        values.priceAfterDiscount = values.price - (values.price * values.discountPercentage) / 100;
        return values;
      }}
      component="section"
    >
      <CustomForm sanitizeEmptyValues={false} />
    </Edit>
  );
};

interface CustomEditProps {
  [key: string]: any;
}

export default ProductsEdit;
